import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'

// Layout (chargé immédiatement car utilisé partout)
import Layout from './components/Layout'

// Lazy loading des pages pour optimiser le code-splitting
// Pages publiques (chargées rapidement)
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const PasswordSetupPage = lazy(() => import('./pages/PasswordSetupPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'))

// Pages protégées (chargées à la demande)
const HomePage = lazy(() => import('./pages/HomePage'))
const SearchPage = lazy(() => import('./pages/SearchPage'))
const QuickBookingPage = lazy(() => import('./pages/QuickBookingPage'))
const BookingPage = lazy(() => import('./pages/BookingPage'))
const MobileReservationForm = lazy(() => import('./pages/MobileReservationForm'))
const TicketsPage = lazy(() => import('./pages/TicketsPage'))
const TicketDetailPage = lazy(() => import('./pages/TicketDetailPage'))
const PackagesPage = lazy(() => import('./pages/PackagesPage'))
const PackageDetailPage = lazy(() => import('./pages/PackageDetailPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const StatsPage = lazy(() => import('./pages/StatsPage'))
const HistoryPage = lazy(() => import('./pages/HistoryPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const BusTrackingPage = lazy(() => import('./pages/BusTrackingPage'))
const AvailableTripsPage = lazy(() => import('./pages/AvailableTripsPage'))
const SuggestionPage = lazy(() => import('./pages/SuggestionPage'))
const PaymentConfirmation = lazy(() => import('./pages/PaymentConfirmation'))
const PaymentCancelled = lazy(() => import('./pages/PaymentCancelled'))

// Composant de chargement pour Suspense
const LoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center gradient-primary">
    <div className="animate-spin w-12 h-12 border-4 border-white border-t-transparent rounded-full" />
  </div>
)

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, refreshUser } = useAuth()
  const [hasCheckedToken, setHasCheckedToken] = React.useState(false)

  // Attendre que l'AuthContext soit initialisé
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated && !hasCheckedToken) {
      // Essayer de récupérer le token avec plusieurs tentatives avant de rediriger
      const checkToken = async () => {
        // Vérifier localStorage et sessionStorage
        let token = localStorage.getItem('token') || sessionStorage.getItem('token')

        // Si pas de token, attendre un peu et réessayer (max 1.5 secondes)
        // (le token peut arriver tardivement au retour de FedaPay)
        if (!token) {
          const startTime = Date.now()
          const maxWaitTime = 1500
          const checkInterval = 200

          while (!token && Date.now() - startTime < maxWaitTime) {
            await new Promise(resolve => setTimeout(resolve, checkInterval))
            token = localStorage.getItem('token') || sessionStorage.getItem('token')
          }
        }

        // Si un token existe mais que l'utilisateur n'est pas chargé (échec réseau
        // transitoire au retour du paiement mobile money), réessayer de restaurer
        // la session AVANT de rediriger vers /login.
        // IMPORTANT : au retour d'un paiement mobile money, le réseau data peut
        // mettre PLUSIEURS SECONDES à revenir (le téléphone sort de l'app opérateur
        // / USSD). On réessaie donc longtemps (~12 s) tant qu'un token valide existe.
        // On ne bascule vers /login QUE si le token est réellement invalide
        // (refreshUser efface le token sur un 401/403 du serveur).
        if (token) {
          for (let i = 0; i < 8; i++) {
            const ok = await refreshUser()
            if (ok) break
            // Token effacé par refreshUser => session réellement invalide (401/403)
            const stillHasToken = localStorage.getItem('token') || sessionStorage.getItem('token')
            if (!stillHasToken) break
            // Sinon : erreur réseau transitoire => attendre et réessayer
            await new Promise(resolve => setTimeout(resolve, 1500))
          }
        }

        setHasCheckedToken(true)
      }

      checkToken()
    } else if (isLoading) {
      setHasCheckedToken(false)
    }
  }, [isLoading, isAuthenticated, hasCheckedToken, refreshUser])

  if (isLoading || (!isAuthenticated && !hasCheckedToken)) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-primary">
        <div className="animate-spin w-12 h-12 border-4 border-white border-t-transparent rounded-full" />
      </div>
    )
  }

  // Préserver la destination pour y revenir après login (ex: retour paiement → /tickets)
  return isAuthenticated
    ? <>{children}</>
    : <Navigate to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`} replace />
}

const App: React.FC = () => {
  return (
    <div className="app">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/password-setup" element={<PasswordSetupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* Protected routes */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Layout>
                <HomePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Layout>
                <SearchPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/quick-booking"
          element={
            <ProtectedRoute>
              <Layout>
                <QuickBookingPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/booking"
          element={
            <ProtectedRoute>
              <Layout>
                <BookingPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reservation"
          element={
            <ProtectedRoute>
              <Layout>
                <MobileReservationForm />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment/confirmation"
          element={<PaymentConfirmation />}
        />
        <Route
          path="/payment/cancelled"
          element={<PaymentCancelled />}
        />
        <Route
          path="/tickets"
          element={
            <ProtectedRoute>
              <Layout>
                <TicketsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <TicketDetailPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/packages"
          element={
            <ProtectedRoute>
              <Layout>
                <PackagesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/packages/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <PackageDetailPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <ProfilePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/stats"
          element={
            <ProtectedRoute>
              <Layout>
                <StatsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <Layout>
                <HistoryPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Layout>
                <NotificationsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-tracking"
          element={
            <ProtectedRoute>
              <Layout>
                <BusTrackingPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/available-trips"
          element={
            <ProtectedRoute>
              <Layout>
                <AvailableTripsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/suggestion"
          element={
            <ProtectedRoute>
              <Layout>
                <SuggestionPage />
              </Layout>
            </ProtectedRoute>
          }
        />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  )
}

export default App

